import type {
  BorrowTermSheet,
  SupplyTermSheet,
  TermSheet,
  TermTag,
} from './types'

/**
 * Tag derivation — PURE, and the only place tags are produced.
 *
 * Tags are derived from the structured fields rather than hand-written per
 * lender, so they can never drift from the numbers they summarize. The
 * validator asserts the round-trip (a tag present ⇒ the field that implies it
 * is present), which turns a derivation bug into a CI failure instead of a
 * misleading badge.
 */

const push = (set: Set<TermTag>, ...tags: TermTag[]) => {
  for (const t of tags) set.add(t)
}

/** Shared market-level tags — governance, oracle, caps. */
function marketTags(
  sheet: Pick<TermSheet, 'governance' | 'oracle'>,
): TermTag[] {
  const out = new Set<TermTag>()
  const { governance: g, oracle: o } = sheet

  if (g) {
    if (g.mutability === 'immutable') push(out, 'immutable')
    if (g.mutability === 'governed') {
      // A non-TIMELOCK root cannot enforce a delay at all → no notice period.
      // A TIMELOCK root with an UNKNOWN delay is not the same thing, and must
      // not be tagged as if it were.
      // A delay read off the market contract itself is enforced by that
      // contract, whoever the owner is — so a vault with an on-chain `timelock`
      // must NOT be tagged `no-timelock` merely because its curator is a Safe.
      const contractEnforced =
        (g.timelockSecs ?? 0) > 0 && g.timelockSource === 'on-chain'
      const isTimelockRoot = g.controllerKind === 'TIMELOCK'
      if (contractEnforced) {
        // a real notice period exists — no tag
      } else if (!isTimelockRoot) push(out, 'no-timelock')
      else if (!g.timelockSecs && !g.timelockUnknown) push(out, 'no-timelock')
      if (g.controllerKind === 'EOA') push(out, 'eoa-controlled')
    }
  }

  if (o) {
    if (o.kind === 'none') push(out, 'no-oracle')
    if (o.kind === 'nav-attested') push(out, 'nav-attested')
    if (o.band === 'HIGH' || o.band === 'CRITICAL') push(out, 'oracle-flagged')
    if (o.flags?.some((f) => f === 'wrong-asset' || f === 'correlated-proxy'))
      push(out, 'oracle-flagged')
  }

  return [...out]
}

function rateTags(rate: SupplyTermSheet['rate']): TermTag[] {
  const out = new Set<TermTag>()
  switch (rate.kind) {
    case 'fixed-term':
    case 'fixed-open':
      push(out, 'fixed-rate')
      break
    case 'user-set':
      push(out, 'user-set-rate')
      break
    case 'zero-interest':
      push(out, 'zero-interest')
      break
    case 'prepaid':
      push(out, 'prepaid-interest')
      break
    case 'nav-accrual':
      push(out, 'nav-accrual')
      break
    case 'variable-curve':
    case 'variable-managed':
      push(out, 'variable-rate')
      break
    default:
      // Unknown kind from a newer producer — emit nothing rather than guess.
      break
  }
  if (rate.rewards?.some((r) => r.indicative || r.kind === 'points'))
    push(out, 'points-rewards')

  // WHERE the yield comes from, not just how much of it there is.
  //
  // `aprTotal` folds market interest, rewards and the asset's own yield into
  // one number, and a supplier reading it cannot tell whether this market pays
  // them or whether they would have earned the same holding the token. Both
  // tags are derived from the components rather than asserted by an adapter,
  // so every lender gets them for free.
  const total = rate.aprTotal
  const base = rate.components?.base ?? 0
  const intrinsic = rate.components?.intrinsic ?? 0
  if (total > 0) {
    // "Mostly" = at least half. Below that the market rate is still the story.
    if (intrinsic > 0 && intrinsic >= total / 2) push(out, 'intrinsic-yield')
    // A market paying no interest of its own. Use a small epsilon rather than
    // `=== 0`: a curve at ~0 % utilization returns a dust APR, not a clean 0.
    if (base <= 0.0001 && intrinsic > 0) push(out, 'no-market-interest')
  }
  return [...out]
}

function maturityTags(maturity: SupplyTermSheet['maturity']): TermTag[] {
  const out = new Set<TermTag>()
  if (maturity.kind === 'fixed-date') push(out, 'has-maturity')
  if (maturity.kind === 'perpetual') push(out, 'perpetual')
  if (maturity.kind === 'rolling-duration') push(out, 'rolling-duration')
  if (maturity.atMaturity === 'physical-delivery')
    push(out, 'physical-delivery')
  return [...out]
}

function availabilityTags(a: SupplyTermSheet['availability']): TermTag[] {
  const out = new Set<TermTag>()
  if (a.gating !== 'permissionless') push(out, 'permissioned')
  if (a.cap != null || a.capUtilization != null) push(out, 'capped')
  if (a.blockedBy === 'cap-full') push(out, 'cap-full')
  return [...out]
}

function counterpartyTags(c: SupplyTermSheet['counterparty']): TermTag[] {
  const out = new Set<TermTag>()
  if (c.solvency === 'undercollateralized') push(out, 'undercollateralized')
  if (c.solvency === 'tranched-junior') push(out, 'first-loss')
  if (c.solvency === 'nav-attested') push(out, 'nav-attested')
  if (c.socializedLoss) push(out, 'socialized-loss')
  return [...out]
}

/** Supply-side tags. Market-level tags are folded in by the caller. */
export function deriveSupplyTags(
  supply: SupplyTermSheet,
  market: Pick<TermSheet, 'governance' | 'oracle'> = {},
): TermTag[] {
  const out = new Set<TermTag>()
  push(out, ...rateTags(supply.rate))
  push(out, ...maturityTags(supply.maturity))
  push(out, ...availabilityTags(supply.availability))
  push(out, ...counterpartyTags(supply.counterparty))
  push(out, ...marketTags(market))

  switch (supply.exit.mode) {
    case 'instant':
      push(out, 'exit-instant')
      break
    case 'instant-capped':
    case 'instant-or-queued':
      push(out, 'exit-capped')
      break
    case 'fixed-cooldown':
      push(out, 'exit-cooldown')
      break
    case 'queued':
    case 'request-based':
    case 'fee-or-queued':
      push(out, 'exit-queued')
      break
    case 'market-sale':
      push(out, 'exit-market-sale')
      break
    default:
      break
  }
  if (supply.exit.priceRisk === 'may-be-impossible')
    push(out, 'exit-may-be-impossible')

  for (const r of supply.principal.risks) {
    if (r === 'first-loss') push(out, 'first-loss')
    if (r === 'physical-delivery') push(out, 'physical-delivery')
  }

  return [...out]
}

/** Borrow-side tags. */
export function deriveBorrowTags(
  borrow: BorrowTermSheet,
  market: Pick<TermSheet, 'governance' | 'oracle'> = {},
): TermTag[] {
  const out = new Set<TermTag>()
  push(out, ...rateTags(borrow.rate))
  push(out, ...maturityTags(borrow.maturity))
  push(out, ...availabilityTags(borrow.availability))
  push(out, ...counterpartyTags(borrow.counterparty))
  push(out, ...marketTags(market))

  push(out, borrow.debtShape === 'accruing' ? 'accruing-debt' : 'static-debt')

  const trigger = borrow.liquidation.trigger
  if (trigger === 'price') push(out, 'price-liquidation')
  if (trigger === 'time') push(out, 'time-liquidation')
  if (trigger === 'price-and-time')
    push(out, 'price-liquidation', 'time-liquidation')
  if (trigger === 'redemption') push(out, 'redeemable')
  if (trigger === 'none') push(out, 'no-liquidation')
  if (borrow.liquidation.redeemable) push(out, 'redeemable')
  if (borrow.liquidation.seizure === 'full-collateral')
    push(out, 'full-collateral-seizure')

  // A repay lock outranks the early-repay class: `free` is true only AFTER the
  // window, and a chip saying "repay any time" beside one saying "locked" is
  // worse than the lock alone. Emit the lock and skip `early-exit-free`.
  const repayLocked = (borrow.exit.cooldownSecs ?? 0) > 0
  if (repayLocked) push(out, 'repay-locked')

  switch (borrow.exit.earlyRepay) {
    case 'free':
      if (!repayLocked) push(out, 'early-exit-free')
      break
    case 'penalty':
      push(out, 'early-exit-penalty')
      break
    case 'discount':
      push(out, 'early-exit-discount')
      break
    default:
      break
  }
  if (borrow.maturity.atMaturity === 'default-seizure')
    push(out, 'full-collateral-seizure')

  return [...out]
}
